import { Component, EventEmitter, HostListener, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatIconModule } from '@angular/material/icon';
import { Router } from '@angular/router';
import { Subject, merge, takeUntil } from 'rxjs';
import { ChatWindowComponent } from '../chat-window/chat-window.component';
import { ChatInputComponent } from '../chat-input/chat-input.component';
import { ChatMessage, ChatView, mapChatHistoryMessage, suggestionChipLabels, isChatbotDisabled, copilotUnavailableMessageKey } from '../../ai-counselor.model';
import { AiCounselorService } from '../../../../../../services/ai-counselor.service';
import { GamificationService } from '../../../../../../services/gamification.service';
import { AiBadgeCelebrationService } from '../../../../../../shared/services/ai-badge-celebration.service';
import { AuthService } from '../../../../../../services/auth.service';
import { IconComponent } from '../../../../../../shared/components/icon/icon.component';
import { getGreetingKey, GreetingKey } from '../../../../../../utils/greeting.util';
import { UserRole } from '../../../../../../core/constants/keys.constant';

const POS_STORAGE_KEY  = 'copilot-panel-pos';
const SIZE_STORAGE_KEY = 'copilot-panel-size';
const DEFAULT_WIDTH  = 440;
const DEFAULT_HEIGHT = 680;
const MIN_WIDTH  = 340;
const MIN_HEIGHT = 440;
const MAX_WIDTH  = 760;
const EDGE_MARGIN = 16;
const DRAG_THRESHOLD = 5;
const MOBILE_BREAKPOINT = 480;

/** Compact, movable/resizable floating panel that reuses the existing AI Counsellor chat experience. */
@Component({
  selector: 'mindbase-copilot-panel',
  standalone: true,
  imports: [CommonModule, TranslateModule, MatTooltipModule, MatIconModule, ChatWindowComponent, ChatInputComponent, IconComponent],
  templateUrl: './copilot-panel.component.html',
  styleUrl: './copilot-panel.component.scss'
})
export class CopilotPanelComponent implements OnChanges, OnInit, OnDestroy {
  private readonly destroy$ = new Subject<void>();
  /** Cancels any in-flight stream when a new one starts or the conversation is reset. */
  private readonly cancelStream$ = new Subject<void>();

  @Input() isOpen = false;
  @Output() closed = new EventEmitter<void>();

  view: ChatView = 'welcome';
  messages: ChatMessage[] = [];
  isSending = false;
  /** True while the chatbot is unavailable (GET /user/chatbot disable window) — replaces the panel body with a redirect prompt. */
  isChatbotBlocked = false;
  private activeConversationId: string | null = null;
  /** Latest session id from the shared state while the panel is closed/hidden — applied once it opens. */
  private latestKnownSessionId: string | null = null;

  protected posX = 0;
  protected posY = 0;
  protected width = DEFAULT_WIDTH;
  protected height = DEFAULT_HEIGHT;
  protected isDragging = false;
  protected isResizing = false;

  private dragActive = false;
  private hasDragged = false;
  private startPointerX = 0;
  private startPointerY = 0;
  private startPosX = 0;
  private startPosY = 0;

  private resizeActive = false;
  private startWidth = 0;
  private startHeight = 0;

  get userName(): string {
    const profile = this.authService.currentProfileDetails;
    const first = profile?.firstName ?? '';
    const last = profile?.lastName ?? '';
    const full = `${first} ${last}`.trim();
    return full || profile?.name || '';
  }

  get greetingKey(): GreetingKey {
    return getGreetingKey();
  }

  /** Tutors/counsellors are guiding a student's plans, not their own — swap the closing clause accordingly. */
  get letsFindKey(): string {
    return this.isTutorOrCounselor ? 'LetsFindTutor' : 'LetsFind';
  }

  get suggestions(): string[] {
    return suggestionChipLabels(this.isTutorOrCounselor);
  }

  private get isTutorOrCounselor(): boolean {
    const role = this.authService.currentProfileDetails?.role;
    return role === UserRole.TUTOR || role === UserRole.COUNSELOR;
  }

  /** Tutors/counsellors have no tutor/counsellor of their own to fall back on — they're pointed to admin instead. */
  get unavailableMessageKey(): string {
    return copilotUnavailableMessageKey(this.isTutorOrCounselor);
  }

  get hasConversation(): boolean {
    return this.messages.length > 0;
  }

  constructor(
    private readonly aiCounselorService: AiCounselorService,
    private readonly gamificationService: GamificationService,
    private readonly aiBadgeCelebrationService: AiBadgeCelebrationService,
    private readonly authService: AuthService,
    private readonly router: Router
  ) {}

  ngOnInit(): void {
    this.loadSize();
    this.loadPosition();

    // Shared with the AI Counsellor page via AiCounselorService — keeps this popup
    // in sync with whichever conversation was most recently active there.
    this.aiCounselorService.activeSessionId$
      .pipe(takeUntil(this.destroy$))
      .subscribe((sessionId) => {
        if (this.isOpen) {
          this.syncActiveSession(sessionId);
        } else {
          this.latestKnownSessionId = sessionId;
        }
      });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['isOpen'] && this.isOpen) {
      // Force a reload on every open — the id may be unchanged from last time
      // the panel was open, but new messages may have been sent on the AI
      // Counsellor page for that same session since then.
      this.syncActiveSession(this.latestKnownSessionId, true);
      this.checkChatbotAvailability();
    }
  }

  /** GET /user/chatbot — re-checked on every open so a mid-session disable window is picked up. */
  private checkChatbotAvailability(): void {
    this.aiCounselorService
      .getChatbotStatus()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res) => {
          this.isChatbotBlocked = isChatbotDisabled(res.data?.chatbot);
        },
        error: () => {
          this.isChatbotBlocked = false;
        },
      });
  }

  /** Reconciles this panel's conversation with the app-wide active session — called on open and on every change. */
  private syncActiveSession(sessionId: string | null, forceRefresh = false): void {
    if (!forceRefresh && sessionId === this.activeConversationId) return;
    this.cancelStream$.next();
    this.activeConversationId = sessionId;
    this.isSending = false;

    if (!sessionId) {
      this.view = 'welcome';
      this.messages = [];
      return;
    }

    this.view = 'chat';
    this.aiCounselorService
      .getMessages(sessionId)
      .pipe(takeUntil(merge(this.destroy$, this.cancelStream$)))
      .subscribe({
        next: (res) => {
          this.messages = (res.data?.messages ?? []).map(mapChatHistoryMessage);
        },
      });
  }

  // ── Chat orchestration ───────────────────────────────────────────────────────

  onSendMessage(text: string): void {
    if (!text.trim() || this.isSending || this.isChatbotBlocked) return;
    this.cancelStream$.next();

    this.view = 'chat';
    this.isSending = true;

    const userMsg: ChatMessage = {
      id: Date.now().toString(),
      role: 'user',
      content: text,
      timestamp: new Date(),
    };

    const botMsgId = `bot-${Date.now()}`;
    const botMsg: ChatMessage = {
      id: botMsgId,
      role: 'bot',
      content: '',
      timestamp: new Date(),
      isStreaming: true,
    };

    this.messages = [...this.messages, userMsg, botMsg];

    let pendingBadgeKeys: string[] | null = null;
    let pendingTrophyKeys: string[] | null = null;

    this.aiCounselorService
      .streamMessage({
        chat_session_id: this.activeConversationId ?? '',
        message: text,
      })
      .pipe(takeUntil(merge(this.destroy$, this.cancelStream$)))
      .subscribe({
        next: (chunk) => {
          if (chunk.chat_session_id) {
            this.activeConversationId = chunk.chat_session_id;
            this.aiCounselorService.setActiveSessionId(chunk.chat_session_id);
          }

          // Reported by the stream's `event: badge` block — resolved and celebrated
          // once the response finishes streaming, so the popup never overlaps it.
          if (chunk.badge_keys?.length) {
            pendingBadgeKeys = chunk.badge_keys;
          }
          if (chunk.newly_earned_trophies?.length) {
            pendingTrophyKeys = chunk.newly_earned_trophies;
          }

          const token = chunk.delta ?? chunk.content ?? '';
          if (token) {
            this.messages = this.messages.map(m =>
              m.id === botMsgId ? { ...m, content: m.content + token } : m
            );
          }
        },
        error: () => {
          this.messages = this.messages.map(m =>
            m.id === botMsgId ? { ...m, isStreaming: false } : m
          );
          this.isSending = false;
          this.checkChatbotAvailability();
        },
        complete: () => {
          this.messages = this.messages.map(m =>
            m.id === botMsgId ? { ...m, isStreaming: false } : m
          );
          this.isSending = false;
          if (pendingBadgeKeys?.length || pendingTrophyKeys?.length) {
            this.celebrateAwardedBadges(pendingBadgeKeys ?? [], pendingTrophyKeys ?? []);
          }
          this.checkChatbotAvailability();
        },
      });
  }

  /** Resolves badge/trophy keys reported by the chat stream and, for any newly
   *  awarded ones, opens the shared AI badge celebration popup. */
  private celebrateAwardedBadges(badgeKeys: string[], trophyKeys: string[]): void {
    this.gamificationService
      .getBadgeDetails(badgeKeys, trophyKeys)
      .pipe(takeUntil(merge(this.destroy$, this.cancelStream$)))
      .subscribe({
        next: (data) => {
          const awardedBadges = (data.badges ?? []).filter((b) => b.assigned);
          const awardedTrophies = (data.trophies ?? []).filter((t) => t.assigned);
          this.aiBadgeCelebrationService.open({
            badges: awardedBadges.map((b) => ({ badge_id: b.badge.badge_id, name: b.badge.name, icon: b.badge.icon })),
            trophies: awardedTrophies.map((t) => ({ name: t.trophy.name, icon: t.trophy.icon })),
          });
        },
        error: () => { /* non-critical — a missed badge popup shouldn't surface an error */ },
      });
  }

  onSuggestionSelected(text: string): void {
    this.onSendMessage(text);
  }

  // ── Header actions ───────────────────────────────────────────────────────────

  startNewChat(): void {
    if (!this.hasConversation) return;
    this.cancelStream$.next();
    this.view = 'welcome';
    this.messages = [];
    this.activeConversationId = null;
    this.aiCounselorService.setActiveSessionId(null);
    this.isSending = false;
  }

  /** Navigates to the full page, resuming the same session if one is active, and drops back to the site-wide fab. */
  openFullPage(): void {
    // Belt-and-suspenders: the shared session is normally already in sync (it's set
    // alongside activeConversationId whenever it changes), but pushing it here too
    // guarantees the AI Counsellor page — which now treats this as the source of
    // truth — opens exactly the session this panel is showing.
    this.aiCounselorService.setActiveSessionId(this.activeConversationId);
    this.router.navigate(['/ai-counselor'], {
      queryParams: this.activeConversationId ? { sessionId: this.activeConversationId } : undefined
    });
    this.closed.emit();
  }

  /** Hides the panel and drops back to the site-wide fab, same as the old close button did. */
  minimize(): void {
    this.closed.emit();
  }

  /** Blocked-state prompt action — sends the student to the tutor/counsellor chat instead. */
  goToChat(): void {
    this.router.navigate(['/chat']);
    this.closed.emit();
  }

  // ── Position persistence (drag) ──────────────────────────────────────────────

  private isMobileViewport(): boolean {
    return window.innerWidth <= MOBILE_BREAKPOINT;
  }

  private loadPosition(): void {
    try {
      const saved = localStorage.getItem(POS_STORAGE_KEY);
      if (saved) {
        const { x, y } = JSON.parse(saved) as { x: number; y: number };
        this.posX = x;
        this.posY = y;
        this.clampPosition();
        return;
      }
    } catch { /* ignore */ }
    this.posX = window.innerWidth - this.width - EDGE_MARGIN * 2;
    this.posY = window.innerHeight - this.height - EDGE_MARGIN * 2;
  }

  private savePosition(): void {
    try {
      localStorage.setItem(POS_STORAGE_KEY, JSON.stringify({ x: this.posX, y: this.posY }));
    } catch { /* ignore */ }
  }

  private clampPosition(): void {
    this.posX = Math.max(EDGE_MARGIN, Math.min(this.posX, window.innerWidth - this.width - EDGE_MARGIN));
    this.posY = Math.max(EDGE_MARGIN, Math.min(this.posY, window.innerHeight - this.height - EDGE_MARGIN));
  }

  onHeaderPointerDown(event: PointerEvent): void {
    if (this.isMobileViewport()) return;
    if ((event.target as HTMLElement).closest('button')) return;
    this.dragActive = true;
    this.hasDragged = false;
    this.startPointerX = event.clientX;
    this.startPointerY = event.clientY;
    this.startPosX = this.posX;
    this.startPosY = this.posY;
    (event.currentTarget as HTMLElement).setPointerCapture(event.pointerId);
  }

  onHeaderPointerMove(event: PointerEvent): void {
    if (!this.dragActive) return;
    const dx = event.clientX - this.startPointerX;
    const dy = event.clientY - this.startPointerY;
    if (!this.hasDragged && Math.hypot(dx, dy) < DRAG_THRESHOLD) return;

    this.hasDragged = true;
    this.isDragging = true;
    this.posX = this.startPosX + dx;
    this.posY = this.startPosY + dy;
    this.clampPosition();
  }

  onHeaderPointerUp(): void {
    this.dragActive = false;
    this.isDragging = false;
    if (this.hasDragged) {
      this.savePosition();
    }
  }

  // ── Size persistence (resize) ────────────────────────────────────────────────

  private loadSize(): void {
    try {
      const saved = localStorage.getItem(SIZE_STORAGE_KEY);
      if (saved) {
        const { w, h } = JSON.parse(saved) as { w: number; h: number };
        this.width = this.clampWidth(w);
        this.height = this.clampHeight(h);
      }
    } catch { /* ignore */ }
  }

  private saveSize(): void {
    try {
      localStorage.setItem(SIZE_STORAGE_KEY, JSON.stringify({ w: this.width, h: this.height }));
    } catch { /* ignore */ }
  }

  private clampWidth(w: number): number {
    const maxAvailable = window.innerWidth - this.posX - EDGE_MARGIN;
    return Math.max(MIN_WIDTH, Math.min(w, Math.min(MAX_WIDTH, maxAvailable)));
  }

  private clampHeight(h: number): number {
    const maxAvailable = window.innerHeight - this.posY - EDGE_MARGIN;
    return Math.max(MIN_HEIGHT, Math.min(h, maxAvailable));
  }

  onResizePointerDown(event: PointerEvent): void {
    if (this.isMobileViewport()) return;
    event.stopPropagation();
    this.resizeActive = true;
    this.startPointerX = event.clientX;
    this.startPointerY = event.clientY;
    this.startWidth = this.width;
    this.startHeight = this.height;
    (event.currentTarget as HTMLElement).setPointerCapture(event.pointerId);
  }

  onResizePointerMove(event: PointerEvent): void {
    if (!this.resizeActive) return;
    this.isResizing = true;
    const dx = event.clientX - this.startPointerX;
    const dy = event.clientY - this.startPointerY;

    // Handle sits at the bottom-right corner, so the top-left corner (posX/posY)
    // stays fixed and only width/height grow with the pointer.
    this.width = this.clampWidth(this.startWidth + dx);
    this.height = this.clampHeight(this.startHeight + dy);
  }

  onResizePointerUp(): void {
    if (this.isResizing) {
      this.saveSize();
    }
    this.resizeActive = false;
    this.isResizing = false;
  }

  @HostListener('window:resize')
  onWindowResize(): void {
    this.width = this.clampWidth(this.width);
    this.height = this.clampHeight(this.height);
    this.clampPosition();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
